import { useMemo } from 'react';
import Link from 'next/link';
import { FormattedMessage } from 'react-intl';

import { ThemeDirection } from '@/core/constants/constants';
import {
  Color,
  DataType,
} from '@/core/interfaces/common';
import {
  ArrowLeft,
  ArrowRight,
} from '@/core/components/Icons';
import { useTheme } from '@/core/hooks/useTheme';
import { HoverImageWrapper } from '@/core/components/HoverImageWrapper';
import { searchMessages } from '@/core/messages/search.messages';
import { Chip } from '@/core/components/Chip';
import { ArticleCaption } from '@/core/components/ArticleCaption';
import { IconButton } from '@/core/components/IconButton';
import { ButtonVariant } from '@/core/components/Button';

import { ArticleLink as ArticleLinkType } from '@/features/Articles/Articles.types';

import { getBackendImage } from '@/utils/helpers';

import {
  ChipWrapper,
  Content,
  IconWrapper,
  ResourceImage,
  Subtitle,
  Title,
  Wrapper,
} from '../ResourceLink.styles';

type ArticleLinkProps = {
  data: DataType<ArticleLinkType>;
  isSearchResult: boolean;
}

export const ArticleLink = ({
  data,
  isSearchResult = false,
}: ArticleLinkProps) => {
  const theme = useTheme();

  const {
    attributes: {
      image,
      title,
      subtitle,
      date,
      link,
      readingTime,
      topic,
    },
  } = data;

  const iconContent = useMemo(() => (
    <IconWrapper>
      <IconButton
        icon={theme.direction === ThemeDirection.LTR ? <ArrowRight /> : <ArrowLeft />}
        variant={ButtonVariant.SECONDARY}
        ariaLabel="go to link"
      />
    </IconWrapper>
  ), [theme.direction]);

  return (
    <Wrapper $withoutLastBorder={!isSearchResult}>
      <HoverImageWrapper>
        <ResourceImage
          src={getBackendImage(image?.data?.attributes?.url, Color.DARK_GREY)}
          alt={image?.data?.attributes?.alternativeText || title}
        />
      </HoverImageWrapper>
      <Content>
        <Link
          href={link as string}
          title={`link to ${title}`}
        >
          <Title>
            {title}
          </Title>
        </Link>
        <Subtitle>{subtitle}</Subtitle>
        {!isSearchResult && (
          <ArticleCaption
            date={date}
            readingTime={readingTime}
            topic={topic}
          />
        )}
        {isSearchResult && (
          <ChipWrapper>
            <Chip color={Color.BLUE}>
              {theme.direction === ThemeDirection.LTR ?
                <ArrowRight size={16} /> :
                <ArrowLeft size={16} />
                  }
              <FormattedMessage {...searchMessages.article} />
            </Chip>
          </ChipWrapper>
        )}
      </Content>
      <Link
        href={link as string}
        title={`link to ${title}`}
      >
        {iconContent}
      </Link>
    </Wrapper>
  );
};
